import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
} from '@angular/core';
import { AbstractControl, FormBuilder, Validators } from '@angular/forms';
import { GpuFormFieldAppearance } from '@geneplanet/ngx-ui/src/lib/form-field';
import {
  BloodMarkerUnit,
  Marker,
} from 'src/app/analyses/interfaces/analyses.types';
import {
  objectToDate,
  subtractCurrentDateYear,
} from 'src/app/shared/util/date-util';
import { BloodAddBase } from '../../interfaces/blood-add-base.interface';
import errorCodes from 'src/app/shared/constants/error-codes';
import { fromToValidator } from 'src/app/analyses/components/add/from-to.validator';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BloodResultValue } from '../../interfaces/blood-analysis-result.interface';
import { BloodConfigurationValue } from '../../interfaces/blood-configuration.interface';
import { AnalyticsService } from '@geneplanet/ngx-utils/src/lib/analytics';
import { ngbDateFromDate, ngbDateToISO } from 'src/app/shared/util/date-util';
import { ngbDateFromISO } from 'src/app/shared/util/date-util';
import { Subject } from 'rxjs';
import { BloodPost } from '../../interfaces/blood-post.inteface';

@UntilDestroy()
@Component({
  selector: 'app-blood-add-value',
  templateUrl: './blood-add-value.component.html',
  styleUrls: ['./blood-add-value.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BloodAddValueComponent implements OnInit, BloodAddBase {
  public configuration: BloodConfigurationValue;
  public analysisResult: BloodResultValue;
  public formFieldAppearance: GpuFormFieldAppearance;
  public showDate = true;
  public submitted = new Subject<void>();

  public form = this.fb.nonNullable.group(
    {
      value: ['', Validators.required],
      from: [''],
      to: [''],
      unit: [''],
      date: [ngbDateFromDate(new Date()), Validators.required],
    },
    { validators: fromToValidator }
  );

  protected errorCodes = errorCodes;

  protected now = ngbDateFromDate(new Date());
  protected minDate = ngbDateFromDate(subtractCurrentDateYear(10));

  constructor(
    private fb: FormBuilder,
    private cdRef: ChangeDetectorRef,
    private analyticsService: AnalyticsService
  ) {}

  public ngOnInit(): void {
    this.form.controls.unit.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((unitId: string) => {
        this.setValidators(this.getUnitByUnitId(unitId));
      });

    const unitId = () => {
      if (this.analysisResult?.result?.unit) {
        return this.analysisResult.result.unit.id;
      } else if (this.analysisResult?.result?.markers) {
        return this.analysisResult.result.markers[0].unitId;
      } else {
        return this.configuration.markers[0].units[0].id;
      }
    };
    this.setValidators(this.getUnitByUnitId(unitId()));

    this.trackDate();
    this.trackUnit();
  }

  public init(): void {
    const unit = this.configuration.markers[0].units[0];

    this.form.controls.unit.patchValue(unit.id);

    if (this.analysisResult) {
      this.fillForm();
    }

    this.cdRef.markForCheck();
  }

  public onSubmit() {
    if (this.form.invalid) {
      return null;
    }

    const { from, to, value, unit } = this.form.value;

    const selectedUnit = this.configuration.markers[0].units.find(
      (markerUnit) => markerUnit.id === unit
    );

    const marker: Marker = {
      markerId: this.configuration.markers[0].id,
      unitId: unit,
      value: Number(value),
      from: from !== '' ? Number(from) : null,
      to: to !== '' ? Number(to) : null,
      unitName: selectedUnit.name,
      isVisibleResult: selectedUnit.isVisibleResult,
    };

    const generatedDate = objectToDate(
      (this.form.value.date as any) ?? {}
    ).toISOString();

    const payload: BloodPost = {
      generated: generatedDate,
      markers: [marker],
    };
    return payload;
  }

  protected trackInput(input: string, control: AbstractControl) {
    this.analyticsService.trackFormValue(
      this.analysisResult ? 'analyses-edit' : 'analyses-add',
      input,
      {
        value: control.value,
        isValid: String(control.valid),
      }
    );
  }

  private trackDate() {
    this.form.controls.date.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.analyticsService.trackFormValue(
          this.analysisResult ? 'analyses-edit' : 'analyses-add',
          'date',
          { value: ngbDateToISO(value) }
        );
      });
  }

  private trackUnit() {
    this.form.controls.unit.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe((value) => {
        this.analyticsService.trackFormValue(
          this.analysisResult ? 'analyses-edit' : 'analyses-add',
          'unit',
          { id: value }
        );
      });
  }

  private setValidators(unit: BloodMarkerUnit) {
    this.form.controls.value.clearValidators();
    this.form.controls.from.clearValidators();
    this.form.controls.to.clearValidators();

    this.form.controls.value.addValidators([
      Validators.required,
      Validators.min(unit.minValue),
      Validators.max(unit.maxValue),
    ]);

    this.form.controls.from.addValidators(Validators.min(unit.minValue));
    this.form.controls.to.addValidators(Validators.max(unit.maxValue));

    // Do not emit event as it breaks analytics
    this.form.controls.value.updateValueAndValidity({ emitEvent: false });
    this.form.controls.from.updateValueAndValidity({ emitEvent: false });
    this.form.controls.to.updateValueAndValidity({ emitEvent: false });

    if (!this.showDate) {
      this.form.controls.date.clearValidators();
      this.form.controls.date.updateValueAndValidity({ emitEvent: false });
    }
  }

  private fillForm(): void {
    const marker = this.analysisResult.result.markers[0];

    let optimal = { from: null, to: null } as any;
    if (this.analysisResult.analysisLevels) {
      // When editing marker from lab report it doesn't have analysisLevels
      optimal = this.analysisResult.analysisLevels.find(
        (level) => level.isOptimal
      );
    }

    this.form.patchValue({
      value: String(marker.value),
      from: String(
        marker.referenceValueMin ?? optimal?.from ?? (marker as any).from
      ),
      to: String(marker.referenceValueMax ?? optimal?.to ?? (marker as any).to),
      unit: marker.unitId,
      date: ngbDateFromISO(this.analysisResult.result.generated),
    });
  }

  private getUnitByUnitId(unitId: string): BloodMarkerUnit {
    return this.configuration.markers[0].units.find(
      (item) => item.id === unitId
    );
  }
}
